// EveClosedTradesTable — Historie geschlossener Trades mit Time-Filter
// (Heute / 7d / 30d / Alle).

import { useMemo, useState } from 'react';
import type { Trade } from '@/types/trade';

export type TimeWindow = 'today' | '7d' | '30d' | 'all';

const WINDOW_LABELS: Record<TimeWindow, string> = {
  today: 'Heute',
  '7d': '7 Tage',
  '30d': '30 Tage',
  all: 'Alle',
};

const WINDOW_ORDER: ReadonlyArray<TimeWindow> = ['today', '7d', '30d', 'all'];

interface EveClosedTradesTableProps {
  trades: Trade[];
}

export function EveClosedTradesTable({ trades }: EveClosedTradesTableProps) {
  const [window, setWindow] = useState<TimeWindow>('today');

  const filtered = useMemo(() => filterByWindow(trades, window), [trades, window]);

  return (
    <div className="glass-strong rounded-2xl p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">EVEs geschlossene Trades</h2>
        <div className="flex gap-1 text-[11px]">
          {WINDOW_ORDER.map((w) => {
            const active = w === window;
            return (
              <button
                key={w}
                type="button"
                onClick={() => setWindow(w)}
                className={
                  active
                    ? 'rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-amber-300'
                    : 'rounded-full border border-white/10 px-2 py-0.5 text-neutral-400 hover:bg-white/5'
                }
              >
                {WINDOW_LABELS[w]}
              </button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-xl border border-dashed border-white/10 px-4 py-6 text-center text-sm text-neutral-500">
          Keine geschlossenen Trades in diesem Zeitfenster.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="pb-2 pr-3">Position</th>
                <th className="pb-2 pr-3">gefolgt von</th>
                <th className="pb-2 pr-3">Eröffnet</th>
                <th className="pb-2 pr-3 text-right">Dauer</th>
                <th className="pb-2 pr-3 text-right">G/V</th>
                <th className="pb-2 text-right">Outcome</th>
              </tr>
            </thead>
            <tbody className="text-[14px]">
              {filtered.map((t) => (
                <ClosedTradeRow key={t.id} trade={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ClosedTradeRow({ trade }: { trade: Trade }) {
  const dirTone = trade.direction === 'LONG' ? 'text-emerald-300' : 'text-rose-300';
  const pnlTone =
    (trade.pnlAbsolute ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400';
  const outcome = classifyOutcome(trade);

  return (
    <tr className="border-t border-white/5">
      <td className="py-2 pr-3">
        <span className={`font-mono ${dirTone}`}>
          {trade.asset} {trade.direction}
        </span>
      </td>
      <td className="py-2 pr-3 font-mono text-amber-300">{formatWallet(trade.eveCall)}</td>
      <td className="py-2 pr-3 text-neutral-400">{formatHmm(trade.createdAt)}</td>
      <td className="py-2 pr-3 text-right tabular text-neutral-400">
        {formatDuration(trade.createdAt, trade.closedAt)}
      </td>
      <td className={`py-2 pr-3 text-right tabular ${pnlTone}`}>
        {trade.pnlAbsolute === null ? '—' : formatUsdDelta(trade.pnlAbsolute)}
      </td>
      <td className="py-2 text-right">
        <span
          className={`rounded px-2 py-0.5 text-[11px] ${outcome.bg} ${outcome.text}`}
        >
          {outcome.label}
        </span>
      </td>
    </tr>
  );
}

function filterByWindow(trades: Trade[], window: TimeWindow): Trade[] {
  if (window === 'all') return trades;
  const now = Date.now();
  const cutoffMs = (() => {
    if (window === 'today') {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }
    if (window === '7d') return now - 7 * 24 * 60 * 60 * 1000;
    return now - 30 * 24 * 60 * 60 * 1000;
  })();
  return trades.filter((t) => {
    const closed = t.closedAt ? Date.parse(t.closedAt) : NaN;
    return Number.isFinite(closed) && closed >= cutoffMs;
  });
}

interface OutcomeStyle {
  label: string;
  bg: string;
  text: string;
}

function classifyOutcome(t: Trade): OutcomeStyle {
  if (t.status === 'WHALE_EXIT') {
    const positive = (t.pnlAbsolute ?? 0) >= 0;
    return positive
      ? { label: 'Whale-Close', bg: 'bg-emerald-500/15', text: 'text-emerald-300' }
      : { label: 'Whale-Close', bg: 'bg-rose-500/15', text: 'text-rose-300' };
  }
  if (t.outcome === 'WIN') {
    return { label: 'EVE-Auto-Close', bg: 'bg-emerald-500/15', text: 'text-emerald-300' };
  }
  if (t.outcome === 'LOSS') {
    return { label: 'EVE-Auto-Close', bg: 'bg-rose-500/15', text: 'text-rose-300' };
  }
  return { label: '—', bg: 'bg-neutral-700/40', text: 'text-neutral-400' };
}

function formatWallet(eveCall: string | undefined): string {
  if (!eveCall) return '—';
  const m = eveCall.match(/0x[a-fA-F0-9]{6,}/);
  if (m) return `${m[0].slice(0, 4)}…${m[0].slice(-2)}`;
  return eveCall.length > 12 ? `${eveCall.slice(0, 10)}…` : eveCall;
}

function formatHmm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(startIso: string, endIso: string | null): string {
  if (!endIso) return '—';
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return '—';
  const min = Math.max(0, Math.floor((end - start) / 60000));
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}min`;
}

function formatUsdDelta(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const abs = Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return `${sign}$${abs}`;
}
